import React, { useEffect, useRef, useState } from 'react'
import { ErrorReference } from '../error/error-reference'
import { SEARCH_FIELD_PROPS } from '../component/search-field-props'
import { idQuery, lookupOrganizations, searchOrganizations } from '../org-search/org-search-api'

export const SEARCH_DEBOUNCE_MS = 250
const PAGE = 20

// Inline panel for linking an organization to the connection: content's
// organization search (owner 2026-10-13: never a list of every
// organization) and a Link button per row. Empty, it lists company
// organizations; typing searches every organization by name (personal ones
// too), and "8" or "#8" also finds that id; "Show more" asks for the next
// page. It sits inside the page's FormNew, so it is plain inputs and
// type="button" buttons, and Enter in the search box is swallowed rather
// than submitting (and saving) the connection.
//
// linkedIDs: organization ids already linked (shown as "Linked", disabled).
// linkingID: the id whose POST is in flight. error: the server's message;
// failure: the error behind it, for its reference (error-reference.js).
export const OrgLinkPanel = ({ linkedIDs = [], linkingID, error, failure, onLink, onClose }) => {
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState([])
  const [next, setNext] = useState(null)
  const [status, setStatus] = useState('loading') // idle | loading | error
  const input = useRef(null)
  const seq = useRef(0)
  const needle = query.trim()
  const type = needle ? 'all' : 'company'

  useEffect(() => { if (input.current) input.current.focus() }, [])

  // One page for the current text, after a pause; a newer search always wins.
  useEffect(() => {
    const mine = ++seq.current
    setStatus('loading')
    const t = setTimeout(async () => {
      try {
        const asID = idQuery(needle)
        const [page, byID] = await Promise.all([searchOrganizations({ q: needle, type, limit: PAGE }), asID ? lookupOrganizations([asID]) : new Map()])
        if (seq.current !== mine) return
        const hit = asID && byID.get(asID)
        setRows(hit ? [hit, ...page.data.filter(o => Number(o.id) !== asID)] : page.data)
        setNext(page.next)
        setStatus('idle')
      } catch (err) {
        if (seq.current !== mine) return
        setRows([])
        setNext(null)
        setStatus('error')
      }
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [needle, type])

  const more = async () => {
    const mine = ++seq.current
    setStatus('loading')
    try {
      const page = await searchOrganizations({ q: needle, type, limit: PAGE, cursor: next })
      if (seq.current !== mine) return
      setRows(r => [...r, ...page.data.filter(o => !r.some(x => x.id === o.id))])
      setNext(page.next)
      setStatus('idle')
    } catch (err) {
      if (seq.current === mine) setStatus('error')
    }
  }

  const linked = new Set(linkedIDs.map(Number))

  const handleKeyDown = e => {
    if (e.key === 'Escape') { e.stopPropagation(); onClose() }
    else if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault()
  }

  return (
    <div className="sw-inline-panel" id="sw-link-panel" role="region" aria-label="Link an organization" onKeyDown={handleKeyDown}>
      <div className="sw-inline-head">
        <label className="sw-label" htmlFor="sw-link-search">Find an organization</label>
        <button type="button" className="btn btn-sm sw-btn" onClick={onClose}>Close</button>
      </div>
      <input {...SEARCH_FIELD_PROPS} id="sw-link-search" name="org-link-search" ref={input} className="form-control"
        placeholder="Type part of a name, or an ID" value={query} onChange={e => setQuery(e.target.value)} />
      {error && <p className="text-danger small m-0" role="alert">{error}</p>}
      {error && <ErrorReference error={failure} className="mt-0" />}
      {status === 'error' && <p className="sw-hint m-0">Organizations couldn't be loaded.</p>}
      {status !== 'error' && rows.length === 0
        ? <p className="sw-hint m-0">{status === 'loading' ? 'Loading organizations…' : 'No organizations match.'}</p>
        : rows.length > 0 && <ul className="sw-results" aria-label="organizations">
          {rows.map(o => {
            const isLinked = linked.has(Number(o.id))
            const busy = linkingID === o.id
            return (
              <li key={o.id}>
                <span className="min-w-0 d-flex flex-column">
                  <span className="text-truncate">{o.name}</span>
                  <span className="sw-hint">
                    <span className="sw-mono">#{o.id}</span>
                    {o.personal && <React.Fragment> · personal</React.Fragment>}
                    {o.createdAt && <React.Fragment> · created {new Date(o.createdAt).toLocaleDateString()}</React.Fragment>}
                  </span>
                </span>
                <button type="button" className={`btn btn-sm ${isLinked ? 'sw-btn' : 'sw-btn-primary'}`}
                  disabled={isLinked || linkingID != null} aria-label={isLinked ? `${o.name} is linked` : `Link ${o.name}`}
                  onClick={() => onLink(o)}>
                  {isLinked ? 'Linked' : busy ? 'Linking…' : 'Link'}
                </button>
              </li>
            )
          })}
        </ul>}
      {next &&
        <div>
          <button type="button" className="btn btn-sm sw-btn" disabled={status === 'loading'} onClick={more}>Show more</button>
        </div>}
    </div>
  )
}
